import { useEventListener } from "@vueuse/core";

/**
 * Handles the Escape key so it closes this menu and nothing around it.
 *
 * @param {object} options
 * @param {import("vue").Ref<boolean>} options.isActive - the menu's open state.
 * @param {import("vue").Ref<boolean>} options.isPermanent - whether the menu is always visible.
 * @param {import("vue").Ref<boolean>} options.showBottomSheet - whether the menu renders as a bottom sheet.
 * @returns {{
 *   rememberOpenOnEscape: () => void,
 *   onKeyDownEscape: (event: KeyboardEvent) => void,
 * }}
 */
export function useMenuEscape({ isActive, isPermanent, showBottomSheet }) {
  let openOnEscape = false;
  let escapeReleasePending = false;

  /**
   * Read during capture, because a consumer such as `sp-select` closes the menu from its own Escape
   * listener before the press bubbles to `onKeyDownEscape`.
   */
  function rememberOpenOnEscape() {
    openOnEscape = isActive.value;
  }

  // `window.Modal` closes on the `keyup` of an Escape press.
  function stopEscapeKeyup(event) {
    if (escapeReleasePending && event.key === "Escape") {
      event.stopPropagation();
      escapeReleasePending = false;
    }
  }

  // A key released while the window is out of focus sends the page no `keyup`.
  function forgetEscapePress() {
    escapeReleasePending = false;
  }

  useEventListener(window, "keyup", stopEscapeKeyup, true);
  useEventListener(window, "blur", forgetEscapePress);

  /** Closes a popup that was open when the press began and keeps the press from reaching surrounding elements. */
  function onKeyDownEscape(event) {
    if (isPermanent.value || !openOnEscape) {
      return;
    }

    event.stopPropagation();
    escapeReleasePending = true;

    // The bottom sheet's native dialog closes itself. A popup cancels the press, because a surrounding
    // `<dialog>` closes on any Escape that is not canceled.
    if (!showBottomSheet.value) {
      isActive.value = false;
      event.preventDefault();
    }
  }

  return { rememberOpenOnEscape, onKeyDownEscape };
}
